import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { emotionImagesData, quizQuestions as originalQuestions, emotionOptions } from './data/stage1EmotionQuestions';
import './App.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:7000';

// Shuffle array utility
const shuffleArray = (array) => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

function EmotionCornerStage1() {
  const navigate = useNavigate();
  const location = useLocation();
  const sessionInfo = location.state || {};   // { sessionId, childName, childAge, hospitalLocation }

  // Shuffle questions on component mount
  const [quizQuestions] = useState(() => shuffleArray(originalQuestions));

  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [score, setScore] = useState(0);
  const [answered, setAnswered] = useState(false);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);

  // Accumulated answers for backend submission
  const [answers, setAnswers] = useState([]);
  const [questionStartTime, setQuestionStartTime] = useState(() => Date.now());
  const [stageStartTime] = useState(() => Date.now());
  const [shownImageIds, setShownImageIds] = useState([]);  // Track shown images to avoid repetition

  // Submission state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // Gallery images fetched from backend (admin-uploaded)
  const [galleryImages, setGalleryImages] = useState([]);

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    fetch(`${API_URL}/api/gallery`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : [])
      .then(data => {
        if (Array.isArray(data)) {
          setGalleryImages(data.map(img => ({
            id: img.id,
            emotion: img.emotion,
            image: '📷',
            imageSrc: img.image_data,
          })));
        }
      })
      .catch(() => {});
  }, []);

  // Merge admin gallery images with built-in defaults for the full image pool
  const allEmotionImages = useMemo(() => [...emotionImagesData, ...galleryImages], [galleryImages]);

  const currentImage = useMemo(() => {
    const question = quizQuestions[currentQuestionIndex];
    const matches = allEmotionImages.filter(img => img.emotion === question.correctEmotion && !shownImageIds.includes(img.id || img.imageSrc));
    if (matches.length === 0) {
      // Fallback: if all images shown, allow any image from this emotion
      const allMatches = allEmotionImages.filter(img => img.emotion === question.correctEmotion);
      return allMatches[Math.floor(Math.random() * allMatches.length)];
    }
    return matches[Math.floor(Math.random() * matches.length)];
  }, [currentQuestionIndex, shownImageIds, quizQuestions, allEmotionImages]);

  const currentQuestion = quizQuestions[currentQuestionIndex];
  const progressPercentage = ((currentQuestionIndex + 1) / quizQuestions.length) * 100;
  const isLastQuestion = currentQuestionIndex === quizQuestions.length - 1;

  // ── Submit results to backend ────────────────────────────────────────────

  const submitResults = async (finalAnswers, finalScore) => {
    if (!sessionInfo.sessionId) {
      setSubmitError('No session found — results not saved. Start from Emotion Corner.');
      return;
    }
    setIsSubmitting(true);
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_URL}/api/results`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          session_id: sessionInfo.sessionId,
          stage: 'pretest',
          score: finalScore,
          total: quizQuestions.length,
          answers: finalAnswers,
          total_time_ms: Date.now() - stageStartTime,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to save results');
      setSubmitted(true);
    } catch (err) {
      setSubmitError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Answer handler ───────────────────────────────────────────────────────

  const handleAnswer = (emotion) => {
    if (answered) return;

    const isCorrect = emotion === currentQuestion.correctEmotion;
    const timeTakenMs = Date.now() - questionStartTime;
    const newAnswer = {
      question_index: currentQuestionIndex,
      correct_emotion: currentQuestion.correctEmotion,
      selected_emotion: emotion,
      is_correct: isCorrect,
      time_taken_ms: timeTakenMs,
    };
    const newAnswers = [...answers, newAnswer];
    const newScore = isCorrect ? score + 1 : score;

    setSelectedAnswer(emotion);
    setAnswered(true);
    setShowFeedback(true);
    setAnswers(newAnswers);
    if (isCorrect) setScore(newScore);
    if (isCorrect) setShowCelebration(true);

    const delay = isCorrect ? 1800 : 2800;
    setTimeout(() => {
      if (!isLastQuestion) {
        // Track shown image BEFORE moving to next question to prevent image change during current question
        const imageId = currentImage.id || currentImage.imageSrc;
        setShownImageIds([...shownImageIds, imageId]);

        setCurrentQuestionIndex(i => i + 1);
        setSelectedAnswer(null);
        setAnswered(false);
        setShowFeedback(false);
        setShowCelebration(false);
        setQuestionStartTime(Date.now());
      } else {
        // Last question done — submit
        setShowCelebration(true);
        submitResults(newAnswers, newScore);
      }
    }, delay);
  };

  const handleRestart = () => {
    setCurrentQuestionIndex(0);
    setScore(0);
    setAnswered(false);
    setSelectedAnswer(null);
    setShowFeedback(false);
    setShowCelebration(false);
    setAnswers([]);
    setShownImageIds([]);
    setSubmitted(false);
    setSubmitError('');
  };

  // ── Completion screen ────────────────────────────────────────────────────

  if (showCelebration && isLastQuestion && answered) {
    const pct = Math.round((score / quizQuestions.length) * 100);
    const msg =
      score === quizQuestions.length ? "Perfect! You identified all emotions! 🌟"
      : score >= 8 ? "Great work! You did very well! 👏"
      : score >= 6 ? "Good job! Keep practicing! 💪"
      : "Nice try! Keep practising! 📚";

    return (
      <div className="app-shell">
        <header className="app-header">
          <div className="header-brand">
            <button className="back-home-btn" onClick={() => navigate('/emotion-corner', { state: sessionInfo })} title="Back">← Back</button>
            <div className="brand-icon">🎊</div>
            <span className="brand-title">Pre/Post Test Complete!</span>
          </div>
        </header>

        <main className="app-main" style={{ flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '24px', padding: '32px 24px' }}>

          {/* Score card */}
          <div style={cs.scoreCard}>
            <div style={{ fontSize: '80px', marginBottom: '12px' }}>🏆</div>
            <h2 style={cs.scoreTitle}>Awesome Job{sessionInfo.childName ? `, ${sessionInfo.childName}` : ''}!</h2>
            <div style={cs.scoreBig}>{score} / {quizQuestions.length}</div>
            <div style={{ fontSize: '18px', color: '#0284c7', fontWeight: '700', marginBottom: '8px' }}>{pct}%</div>
            <p style={cs.scoreMsg}>{msg}</p>

            {/* Save status */}
            {isSubmitting && (
              <div style={cs.infoBox}>⏳ Saving results...</div>
            )}
            {submitted && (
              <div style={{ ...cs.infoBox, background: '#F0FDF4', border: '1.5px solid #86EFAC', color: '#166534' }}>
                ✅ Results saved successfully!
              </div>
            )}
            {submitError && (
              <div style={{ ...cs.infoBox, background: '#FFF5F5', border: '1.5px solid #FCA5A5', color: '#991B1B' }}>
                ⚠️ {submitError}
              </div>
            )}

            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', justifyContent: 'center', marginTop: '8px' }}>
              <button onClick={handleRestart} style={cs.btnBlue}>🔄 Try Again</button>
              {submitted && sessionInfo.sessionId && (
                <button
                  onClick={() => navigate(`/report/${sessionInfo.sessionId}`, { state: sessionInfo })}
                  style={cs.btnGreen}
                >
                  📊 View Report
                </button>
              )}
              <button onClick={() => navigate('/emotion-corner', { state: sessionInfo })} style={cs.btnGray}>← Go Back</button>
            </div>
          </div>

          {/* Answer breakdown */}
          <div style={cs.breakdownCard}>
            <h3 style={cs.breakdownTitle}>Answer Breakdown</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {answers.map((a, i) => (
                <div key={i} style={{ ...cs.answerRow, background: a.is_correct ? '#F0FDF4' : '#FFF5F5', borderColor: a.is_correct ? '#86EFAC' : '#FCA5A5' }}>
                  <span style={{ fontWeight: '700', color: '#374151' }}>Q{i + 1}</span>
                  <span style={{ flex: 1, color: '#374151' }}>
                    Correct: <strong>{a.correct_emotion}</strong>
                    {!a.is_correct && <> · You chose: <strong style={{ color: '#DC2626' }}>{a.selected_emotion}</strong></>}
                  </span>
                  <span style={{ fontSize: '18px' }}>{a.is_correct ? '✅' : '❌'}</span>
                </div>
              ))}
            </div>
          </div>
        </main>

        <style>{`@keyframes bounce { 0%,100%{transform:scale(1)} 50%{transform:scale(1.1)} }`}</style>
      </div>
    );
  }

  // ── Quiz screen ──────────────────────────────────────────────────────────

  return (
    <div className="app-shell">
      <header className="app-header">
        <div className="header-brand">
          <button className="back-home-btn" onClick={() => navigate('/emotion-corner', { state: sessionInfo })} title="Back">← Back</button>
          <div className="brand-icon">😊</div>
          <span className="brand-title">Pre/Post Test</span>
        </div>
        <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#d97706' }}>
          ⭐ {score}/{quizQuestions.length}
        </div>
      </header>

      <div className="progress-track">
        <div className="progress-fill" style={{ width: `${progressPercentage}%` }}></div>
      </div>

      <main className="app-main">
        {/* LEFT */}
        <div className="left-col">
          <div className="image-card" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg,#f8f9ff,#f0f4ff)', width: '100%', height: '100%', position: 'relative' }}>
            <div style={{ fontSize: '200px', lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
              {currentImage.imageSrc ? (
                <img src={currentImage.imageSrc} alt={currentQuestion.correctEmotion} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '16px' }} />
              ) : (
                currentImage.image
              )}
            </div>
            <div style={{ position: 'absolute', bottom: '15px', fontSize: '14px', color: '#6b7280', fontWeight: '500', background: 'rgba(255,255,255,0.9)', padding: '8px 16px', borderRadius: '20px' }}>
              Question {currentQuestionIndex + 1} of {quizQuestions.length}
            </div>
          </div>
        </div>

        {/* RIGHT */}
        <div className="right-col">
          <div className="question-block">
            <h2 className="question-title">{currentQuestion.question}</h2>
            <p className="question-sub">Tap the emoji that matches the face.</p>
          </div>

          <div className="emotion-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', width: '100%' }}>
            {emotionOptions.map((emotion) => {
              const isSelected = selectedAnswer === emotion.name;
              const isCorrect = answered && emotion.name === currentQuestion.correctEmotion;
              const isWrong = answered && isSelected && !isCorrect;
              return (
                <button
                  key={emotion.name}
                  onClick={() => !answered && handleAnswer(emotion.name)}
                  disabled={answered}
                  className="emotion-btn"
                  style={{
                    background: isCorrect ? '#10b981' : isWrong ? '#ef4444' : emotion.bg,
                    borderColor: isSelected && !answered ? emotion.border : 'transparent',
                    color: isCorrect || isWrong ? 'white' : emotion.text,
                    boxShadow: isCorrect ? '0 0 0 3px #16A34A,0 0 20px rgba(16,185,129,0.8)' : isSelected && !answered ? `0 0 0 3px ${emotion.border}` : '0 2px 8px rgba(0,0,0,0.06)',
                    opacity: answered && !isCorrect && !isWrong ? 0.5 : 1,
                    cursor: answered ? 'not-allowed' : 'pointer',
                    border: '2px solid',
                  }}
                >
                  <span className="em-emoji">{emotion.emoji}</span>
                  <span className="em-name">{emotion.name}</span>
                </button>
              );
            })}
          </div>

          {showFeedback && (
            <div className={`feedback-bar ${selectedAnswer === currentQuestion.correctEmotion ? 'success' : 'error'}`}>
              {selectedAnswer === currentQuestion.correctEmotion
                ? '✓ Correct! Great job! 🎉'
                : `✗ The correct emotion is ${currentQuestion.correctEmotion}. Try again! 💪`}
            </div>
          )}
        </div>
      </main>

      <style>{`@keyframes bounce { 0%,100%{transform:scale(1)} 50%{transform:scale(1.15)} }`}</style>
    </div>
  );
}

// ── Completion styles ─────────────────────────────────────────────────────────
const cs = {
  scoreCard: {
    textAlign: 'center', padding: '40px 40px 32px', borderRadius: '24px',
    background: 'linear-gradient(135deg,#f0fdf4,#dbeafe)', boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
    maxWidth: '560px', width: '100%',
  },
  scoreTitle: { fontSize: '30px', fontWeight: 'bold', color: '#059669', margin: '0 0 16px' },
  scoreBig: { fontSize: '56px', fontWeight: 'bold', color: '#0284c7', margin: '0 0 4px' },
  scoreMsg: { fontSize: '16px', color: '#4a5568', lineHeight: '1.7', margin: '0 0 20px' },
  infoBox: {
    background: '#EFF6FF', border: '1.5px solid #BFDBFE', borderRadius: '10px',
    padding: '10px 16px', fontSize: '14px', fontWeight: '600', color: '#1E40AF',
    marginBottom: '16px',
  },
  btnBlue: {
    padding: '13px 24px', fontSize: '15px', fontWeight: 'bold', color: 'white',
    background: 'linear-gradient(135deg,#0284c7,#0369a1)', border: 'none',
    borderRadius: '12px', cursor: 'pointer', boxShadow: '0 4px 12px rgba(2,132,199,0.3)',
  },
  btnGreen: {
    padding: '13px 24px', fontSize: '15px', fontWeight: 'bold', color: 'white',
    background: 'linear-gradient(135deg,#059669,#047857)', border: 'none',
    borderRadius: '12px', cursor: 'pointer', boxShadow: '0 4px 12px rgba(5,150,105,0.3)',
  },
  btnGray: {
    padding: '13px 24px', fontSize: '15px', fontWeight: 'bold', color: 'white',
    background: 'linear-gradient(135deg,#1e40af,#1e3a8a)', border: 'none',
    borderRadius: '12px', cursor: 'pointer', boxShadow: '0 4px 12px rgba(30,64,175,0.3)',
  },
  breakdownCard: {
    background: '#fff', borderRadius: '20px', padding: '28px 32px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)', maxWidth: '560px', width: '100%',
  },
  breakdownTitle: { fontSize: '18px', fontWeight: '800', color: '#1A2340', margin: '0 0 16px' },
  answerRow: {
    display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 14px',
    borderRadius: '10px', border: '1.5px solid', fontSize: '14px',
  },
};

export default EmotionCornerStage1;
